import {FormProvider, useForm, UseFormProps} from 'react-hook-form';
import {
    FormHTMLAttributes, ForwardedRef,
    forwardRef,
    PropsWithChildren,
    useEffect,
    useImperativeHandle, useMemo,
    useRef,
    useState
} from 'react';
import axios, {AxiosError} from 'axios';
import {UseFormReturn} from 'react-hook-form/dist/types';
import {useAppSelector} from '@hooks/app';
import {noop} from '@utils/constants';
import {QueryMapRules, useMapUrlParams} from '@ui/Form/utils';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {useAuthAxios} from '@hooks/useAuthAxios';
import qs from 'qs';
import moment from 'moment/moment';
import {useDebounce} from 'use-debounce';
import {devBaseUrl, prodBaseUrl} from '../../../api';
import {mapLaravelValidationMessage} from '../../../constants/laravelValidationMessages';

type FormProps = PropsWithChildren<UseFormProps & Omit<FormHTMLAttributes<any>, 'onSubmit' | 'onChange'> & {
    onInvalid?: (values: any) => any;
    onChange?: (values: any) => any;

    beforeSubmit?: (values: any) => any;
    afterSubmit?: (data: any) => any;
    onError?: (data: AxiosError<any>) => any;

    action?: string;
    method?: 'get' | 'post' | 'put' | 'patch' | 'delete',

    form?: UseFormReturn
    type?: 'json' | 'fd'

    storeInUrl?: boolean
    mapQueryParams?: QueryMapRules
    autoSubmit?: boolean
    restoreFromStorage?: () => Record<string, any> | null;
    defaultOrderBy?: string | null;
    requestTimeout?: number;
}>;

type LaravelValidationErrorResponse = {
    message?: string;
    errors?: Record<string, unknown>;
};

export const Form = ({
                         children,
                         onInvalid = noop,
                         afterSubmit = noop,
                         onError = noop,
                         beforeSubmit = v => v,
                         onChange = noop,

                         action: url,
                         method = 'post',

                         form: _form,
                         type = 'json',

                         storeInUrl = false,
                         mapQueryParams = {},
                         restoreFromStorage,
                         defaultOrderBy,

                         // useForm params
                         mode,
                         reValidateMode,
                         defaultValues,
                         resolver,
                         context,
                         shouldFocusError,
                         shouldUnregister,
                         shouldUseNativeValidation,
                         criteriaMode,
                         delayError,
                         autoSubmit = false,
                         requestTimeout,

                         ...formAttrs
                     }: FormProps) => {
    let form = useForm({
        mode,
        reValidateMode,
        defaultValues,
        resolver,
        context,
        shouldFocusError,
        shouldUnregister,
        shouldUseNativeValidation,
        criteriaMode,
        delayError,
    });
    const axios = useAuthAxios();
    const formRef = useRef<HTMLFormElement>();
    const mapParams = useMapUrlParams(mapQueryParams);
    const navigate = useNavigate();
    const location = useLocation();
    const timezone_offset = useMemo(() => (new Date()).getTimezoneOffset(), []);
    const [searchParams] = useSearchParams();

    if (_form) {
        form = _form;
    }

    const handleAJAXError = (error: AxiosError<any>) => {
        const {response} = error;
        if (!response) {
            onError(error);
            return;
        } else if (response.status === 422 && response.data?.['errors']) {
            const data = response.data as LaravelValidationErrorResponse;
            const errors = data?.errors || {};

            for (const [name, fieldErrors] of Object.entries(errors)) {
                const message = mapLaravelValidationMessage(fieldErrors, name);
                form.setError(name as any, {type: 'validate', message}, {shouldFocus: true});
            }
            return;
        } else if (response.status !== 422 && response.data?.['message']) {
            const message = mapLaravelValidationMessage(response.data['message']);
            form.setError('_' as any, {type: 'validate', message}, {shouldFocus: true});
        } else {
            console.error(error);
        }

        onError(error);
    };

    const setUrlParams = (replace = false) => {
        const payload = mapParams(form.watch());
        const search = qs.stringify(payload, {
            serializeDate: d => moment(d)
                .utc(true)
                .subtract(timezone_offset, 'minutes')
                .toISOString()
        });
        if (search === searchParams.toString()) return;
        navigate({
            search,
            hash: location.hash,
        }, {
            relative: 'path',
            replace,
        });
    };

    const fetchData = async () => {
        const value = mapParams(form.watch());

        try {
            let data = await beforeSubmit(value);
            if (data === undefined) {
                data = value;
            }

            if (type === 'fd') {
                data = new FormData(formRef.current);
            }

            if (url) {
                const res = await axios.request({
                    baseURL: process.env.NODE_ENV === 'development'
                        ? devBaseUrl
                        : prodBaseUrl,
                    headers: {
                        'Accept': 'application/json',
                    },
                    method,
                    url,
                    data,
                    params: ['get'].indexOf(method) >= 0 ? data : {},
                    ...(requestTimeout !== undefined ? {timeout: requestTimeout} : {}),
                });
                afterSubmit(res.data);
            } else {
                afterSubmit(data);
            }
        } catch (e: any) {
            handleAJAXError(e);
        }
    };

    const handleSubmit = async () => {
        // form.setValue('page', null);
        if (storeInUrl) {
            setUrlParams();
        } else {
            await fetchData();
        }
    };

    useEffect(() => {
        if (!autoSubmit) {
            return;
        }
        if (storeInUrl) {
            const urlString = searchParams.toString();
            const fromUrl = mapParams(qs.parse(urlString)) as Record<string, any>;

            const withDefaultOrderBy = (values: Record<string, any>): Record<string, any> =>
                defaultOrderBy && !values.order_by
                    ? {...values, order_by: defaultOrderBy}
                    : values;

            if (restoreFromStorage) {
                const saved = restoreFromStorage() as Record<string, any>;
                if (saved) {
                    form.reset(withDefaultOrderBy({
                        ...saved,
                        ...fromUrl,
                        query: {
                            ...(saved.query || {}),
                            ...(fromUrl.query || {}),
                        },
                    }));
                    setUrlParams();
                    return;
                }
            }

            form.reset(withDefaultOrderBy(fromUrl));
            setUrlParams();
        } else {
            fetchData();
        }
    }, [autoSubmit]);

    // useEffect(() => {
    //     if (storeInUrl && autoSubmit) {
    //         form.reset(mapParams(qs.parse(searchParams.toString())));
    //         fetchData();
    //     }
    // }, [searchParams.toString(), autoSubmit]);

    const [debouncedForm] = useDebounce(JSON.stringify(form.watch()), 500);
    const isInitialMount = useRef(true);

    useEffect(() => {
        if(!autoSubmit) {
            return;
        }

        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        // Автопоиск тоже обновляет адрес: после входа форма восстановит именно эти фильтры.
        // replace не создаёт отдельную запись истории на каждое изменение поля.
        if (storeInUrl) setUrlParams(true);
        fetchData();

    }, [debouncedForm]);

    return <FormProvider {...form}>
        <form
            ref={formRef}
            onSubmit={form.handleSubmit(handleSubmit, onInvalid)}
            onChange={() => onChange(mapParams(form.watch()))}
            {...formAttrs}
        >
            {children}
        </form>
    </FormProvider>;
};

